/**
 * Composition root — the ONLY place infrastructure is wired (guide Step 5).
 * Order: stores → push channel → router → provide (above the router root) → dev
 * auditor → start. The push channel opens on demand (the heartbeat surface
 * subscribes when a run starts); resume() re-hydrates from the durable aggregate.
 */

import {
  createKitContext,
  createPushChannel,
  createRouter,
  defineStore,
  effect,
  installDevAuditor,
  type EventSourceLike,
} from "@platform/web-kit"
import { registerElements } from "./elements"
import {
  isSearchRunStream,
  isIngestStream,
  isEvidenceStream,
  isAssetStream,
  isWorkshopStream,
  isOpportunityStream,
  isOrganizationStream,
  isApplicationStream,
  isGenerationRunStream,
  searchRunSpec,
  ingestSpec,
  evidenceSpec,
  assetSpec,
  workshopSpec,
  opportunitySpec,
  orgSpec,
  applicationSpec,
  generationRunSpec,
  createBreadcrumbBus,
  createSectionNavBus,
  publishEvidenceGapIndicator,
  VOCABULARY,
  type ShellSession,
} from "./domain"
import { buildRoutes } from "./routes"
import {
  AssetStoreToken,
  EvidenceStoreToken,
  I18nToken,
  IngestStoreToken,
  OpportunityStoreToken,
  OrgStoreToken,
  PushToken,
  RouterToken,
  SearchRunStoreToken,
  SessionToken,
  WorkshopStoreToken,
  ApplicationStoreToken,
  GenerationRunStoreToken,
  BreadcrumbToken,
  SectionNavToken,
  makeAssetCommandTransport,
  makeEvidenceCommandTransport,
  makeOpportunityCommandTransport,
  makeApplicationCommandTransport,
} from "./services"
import { createCareerI18n, type CareerKey } from "../i18n"

/** Per-surface document title key — drives the "<Title> · Career" tab title. */
const DOC_TITLE: Record<string, CareerKey> = {
  "career-onboarding": "surface.onboarding.title",
  "career-extraction-review": "surface.extraction.title",
  "career-workshop-list": "surface.workshops.title",
  "career-workshop-runner": "surface.workshopRunner.title",
  "career-compass-hub": "surface.kompass.title",
  "career-werte-view": "surface.werte.title",
  "career-cv-print": "surface.lebenslauf.title",
  "career-profile": "surface.profil.title",
  "career-cv-workbench": "surface.lebenslauf.title",
  "career-evidence-ledger": "surface.belege.title",
  "career-assets": "surface.nachweise.title",
  "career-search": "surface.suche.title",
  "career-opportunity-detail": "surface.opportunity.title",
  "career-applications": "surface.bewerbungen.title",
  "career-application-review": "surface.bewerbungen.title",
  "career-runs": "surface.laeufe.title",
  "career-credits": "surface.credits.title",
  "career-llm-settings": "surface.einstellungen.title",
  "career-heartbeat": "heartbeat.title",
  "career-bench-grading": "surface.grading.title",
  "career-bench-lab": "surface.benchLab.title",
  "career-login": "login.title",
  "career-not-found": "notfound.title",
}

async function loadSession(): Promise<ShellSession> {
  try {
    const res = await fetch("/api/auth/me", { headers: { Accept: "application/json" } })
    if (res.ok) return (await res.json()) as ShellSession
  } catch {
    // BFF unreachable — fall through to a signed-out, non-enforced shell.
  }
  return { user: null, devMode: false, authEnforced: false }
}

async function main(): Promise<void> {
  registerElements() // 1. custom elements
  const host = document.getElementById("app")
  if (!host) throw new Error("[career] missing #app host")

  const session = await loadSession()
  const i18n = createCareerI18n()

  const searchRunStore = defineStore(searchRunSpec()) // 2. stores
  const ingestStore = defineStore(ingestSpec())
  const evidenceStore = defineStore(evidenceSpec(makeEvidenceCommandTransport()))
  const assetStore = defineStore(assetSpec(makeAssetCommandTransport()))
  const workshopStore = defineStore(workshopSpec())
  const opportunityStore = defineStore(opportunitySpec(makeOpportunityCommandTransport()))
  const orgStore = defineStore(orgSpec())
  const applicationStore = defineStore(applicationSpec(makeApplicationCommandTransport()))
  const generationRunStore = defineStore(generationRunSpec())
  const breadcrumb = createBreadcrumbBus() // shell-level breadcrumb contribution channel (§7a)
  const sectionNav = createSectionNavBus() // shell-level left-pane within-space nav channel

  /** Route an envelope to the store that owns its aggregate. */
  const storeFor = (streamId: string) =>
    isIngestStream(streamId)
      ? ingestStore
      : isEvidenceStream(streamId)
        ? evidenceStore
        : isAssetStream(streamId)
          ? assetStore
          : isWorkshopStream(streamId)
            ? workshopStore
            : isOpportunityStream(streamId)
              ? opportunityStore
              : isOrganizationStream(streamId)
                ? orgStore
                : isSearchRunStream(streamId)
                  ? searchRunStore
                  : isApplicationStream(streamId)
                    ? applicationStore
                    : isGenerationRunStream(streamId)
                      ? generationRunStore
                      : null

  const push = createPushChannel({ // 3. push plane
    url: "/api/push",
    eventSource: (url) => new EventSource(url) as unknown as EventSourceLike,
    readHeads: async (streamIds) => {
      const res = await fetch(`/api/heads?streams=${encodeURIComponent(JSON.stringify(streamIds))}`)
      if (!res.ok) throw new Error(`heads read failed: HTTP ${res.status}`)
      return (await res.json()) as Array<{ streamId: string; version: number }>
    },
    apply: (envelope) => storeFor(envelope.streamId)?.apply(envelope),
    lastApplied: (streamId) => storeFor(streamId)?.head(streamId) ?? 0,
  })

  const router = createRouter({ routes: buildRoutes(session), root: host, notFound: "career-not-found" }) // 4. router

  // Per-route tab title: "<German surface title> · Career".
  effect([router.current], () => {
    const surface = router.current.value?.chain.at(-1)?.record.surface
    const key = surface ? DOC_TITLE[surface] : undefined
    document.title = key ? `${i18n.t(key)} · ${i18n.t("app.name")}` : i18n.t("app.name")
  })

  const ctx = createKitContext(host) // 5. provide ABOVE the router root
  ctx.provide(SearchRunStoreToken, searchRunStore)
  ctx.provide(IngestStoreToken, ingestStore)
  ctx.provide(EvidenceStoreToken, evidenceStore)
  ctx.provide(AssetStoreToken, assetStore)
  ctx.provide(WorkshopStoreToken, workshopStore)
  ctx.provide(OpportunityStoreToken, opportunityStore)
  ctx.provide(OrgStoreToken, orgStore)
  ctx.provide(ApplicationStoreToken, applicationStore)
  ctx.provide(GenerationRunStoreToken, generationRunStore)
  ctx.provide(RouterToken, router)
  ctx.provide(I18nToken, i18n)
  ctx.provide(SessionToken, session)
  ctx.provide(PushToken, push)
  ctx.provide(BreadcrumbToken, breadcrumb)
  ctx.provide(SectionNavToken, sectionNav)

  // #32: a LIVE open-gap badge on the Belege section, driven from the evidence store (the
  // data owner) — not the shell. Reflects the store app-wide (see publisher docstring).
  if (session.user) publishEvidenceGapIndicator(evidenceStore, sectionNav)

  if (session.devMode) installDevAuditor(host, VOCABULARY) // 6. dev AX auditor
  void router.start() // 7. resolve the current URL
}

void main()
